/**
 * Client payment timing settings — business-day lag between each installment's
 * anchor event and its expected payment date. Stored at
 * `financeSettings/paymentTerms`; read wherever payment due flags are computed
 * (Gantt flags, Ready to Bill queue, cash flow projection).
 */

import { doc, onSnapshot, serverTimestamp, setDoc, type Unsubscribe } from 'firebase/firestore';
import { db } from './firebase';
import {
  DEFAULT_PAYMENT_LAG_SETTINGS,
  type PaymentLagSettings,
} from './paymentDueFlags';

const SETTINGS_COLLECTION = 'financeSettings';
export const PAYMENT_TERMS_SETTINGS_DOC_ID = 'paymentTerms';

function clampLagDays(value: unknown, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(90, Math.max(0, Math.round(n)));
}

export function parsePaymentLagSettings(raw: Record<string, unknown> | undefined): PaymentLagSettings {
  const d = DEFAULT_PAYMENT_LAG_SETTINGS;
  if (!raw) return { ...d };
  return {
    depositLagBusinessDays: clampLagDays(raw.depositLagBusinessDays, d.depositLagBusinessDays),
    preShipmentLagBusinessDays: clampLagDays(
      raw.preShipmentLagBusinessDays,
      d.preShipmentLagBusinessDays,
    ),
    finalLagBusinessDays: clampLagDays(raw.finalLagBusinessDays, d.finalLagBusinessDays),
  };
}

/**
 * Live payment lag settings; missing doc or read errors fall back to defaults
 * so due-date math never blocks on the settings read.
 */
export function subscribePaymentLagSettings(
  onSettings: (settings: PaymentLagSettings) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, SETTINGS_COLLECTION, PAYMENT_TERMS_SETTINGS_DOC_ID),
    snap => onSettings(parsePaymentLagSettings(snap.data())),
    err => {
      console.warn('[paymentTermsSettings] listen failed — using defaults', err);
      onSettings({ ...DEFAULT_PAYMENT_LAG_SETTINGS });
      onError?.(err);
    },
  );
}

export async function savePaymentLagSettings(
  settings: PaymentLagSettings,
  by: { userId: string; userName?: string },
): Promise<void> {
  await setDoc(
    doc(db, SETTINGS_COLLECTION, PAYMENT_TERMS_SETTINGS_DOC_ID),
    {
      depositLagBusinessDays: clampLagDays(
        settings.depositLagBusinessDays,
        DEFAULT_PAYMENT_LAG_SETTINGS.depositLagBusinessDays,
      ),
      preShipmentLagBusinessDays: clampLagDays(
        settings.preShipmentLagBusinessDays,
        DEFAULT_PAYMENT_LAG_SETTINGS.preShipmentLagBusinessDays,
      ),
      finalLagBusinessDays: clampLagDays(
        settings.finalLagBusinessDays,
        DEFAULT_PAYMENT_LAG_SETTINGS.finalLagBusinessDays,
      ),
      updatedAt: serverTimestamp(),
      updatedBy: by.userId,
      ...(by.userName ? { updatedByName: by.userName } : {}),
    },
    { merge: true },
  );
}
